import type { ListBuilderConfiguration } from '../types';

/**
 * Client Contacts Embedded list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const CLIENT_CONTACTS_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-client-contacts-embedded',
			cacheTTL: 300000,
			name: 'Client Contacts',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'primary-star-column',
					fields: [
						{
							id: 'primary-toggle',
							displayStyle: 'primary_contact_toggle',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isPrimary',
									objectTable: 'client_contact',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
						},
					],
					headers: [{ id: 'h-primary', text: '' }],
					width: 40,
					align: 'center',
					sortable: false,
				},
				{
					id: 'name-column',
					fields: [
						{
							id: 'contact-name',
							// PD-17065: the person's contact page (or this tab for a
							// record that is not a person).
							link: { type: 'custom', enabled: true, urlTemplate: '{contactUrl}' },
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'client_contact',
								},
								defaultValue: 'Unnamed Contact',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-name',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'contact-title',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'titleLabel',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-roles',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'contact-roles',
							dataConfig: { type: 'field', field: { fieldName: 'roleNames', objectTable: 'client_contact' } },
							styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'info' } },
							displayStyle: 'badge',
							// Hidden when empty rather than a dash; most people have none.
							conditionalVisibility: { field: 'roleNames', condition: 'is_not_null' },
						},
					],
					headers: [
						{ id: 'h-name', text: 'Name' },
						{ id: 'h-title', text: 'Title' },
					],
					width: 180,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: 'client_contact',
						},
					],
				},
				{
					id: 'contact-info-column',
					fields: [
						{
							id: 'contact-email',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'email',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
								iconName: 'envelope',
							},
							displayStyle: 'text_with_icon',
						},
						{
							id: 'line-break-email',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'contact-phone',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'phone',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'dialPhone',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
								iconName: 'phone',
							},
							displayStyle: 'phone',
						},
						{
							id: 'line-break-mobile',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'contact-mobile',
							dataConfig: { type: 'field', field: { fieldName: 'mobilePhone', objectTable: 'client_contact' } },
							styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
							displayStyle: 'phone',
							conditionalVisibility: { field: 'mobilePhone', condition: 'is_not_null' },
						},
						{
							id: 'line-break-phone',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'contact-fax',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'fax',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
								iconName: 'fax',
							},
							displayStyle: 'text_with_icon',
						},
					],
					headers: [{ id: 'h-contact-info', text: 'Contact Info' }],
					sortable: false,
				},
				{
					// PD-17065: how many records stand behind the person here, and
					// whether they have stopped being active at this customer.
					id: 'status-column',
					fields: [
						{
							// PD-17053: the role exactly as the ERP sends it, and the ship-to
							// (job site) the contact belongs to. Filled by the ERP imports.
							id: 'contact-erp-role',
							dataConfig: { type: 'field', field: { fieldName: 'erpRoleLabel', objectTable: 'client_contact' } },
							styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
							displayStyle: 'text',
							conditionalVisibility: { field: 'erpRoleLabel', condition: 'is_not_null' },
						},
						{
							id: 'line-break-ship-to',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'contact-ship-to',
							dataConfig: { type: 'field', field: { fieldName: 'shipToLabel', objectTable: 'client_contact' } },
							styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
							displayStyle: 'text',
							conditionalVisibility: { field: 'shipToLabel', condition: 'is_not_null' },
						},
						{
							id: 'line-break-records',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'contact-records',
							dataConfig: { type: 'field', field: { fieldName: 'recordsLabel', objectTable: 'client_contact' } },
							styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } },
							displayStyle: 'text',
							conditionalVisibility: { field: 'recordsLabel', condition: 'is_not_null' },
						},
						{
							id: 'line-break-status',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'contact-status',
							dataConfig: { type: 'field', field: { fieldName: 'statusLabel', objectTable: 'client_contact' } },
							styleConfig: { size: 'sm', colorRule: { type: 'theme', themeColor: 'warning' } },
							displayStyle: 'text',
							conditionalVisibility: { field: 'statusLabel', condition: 'is_not_null' },
						},
					],
					headers: [{ id: 'h-status', text: 'Records' }],
					width: 260,
					align: 'left',
					sortable: false,
				},
				{
					id: 'notes-column',
					fields: [
						{
							id: 'contact-note',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'note',
									objectTable: 'client_contact',
								},
								defaultValue: '',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
								textExpansion: {
									enabled: true,
									maxLines: 2,
								},
							},
							displayStyle: 'text',
							truncate: true,
							maxLength: 100,
						},
					],
					headers: [{ id: 'h-notes', text: 'Notes' }],
					sortable: false,
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'open-action',
									label: 'Open Contact',
									iconName: 'eye',
									callbackName: 'openContact',
									variant: 'secondary',
									visibility: 'visible',
									conditionalVisibility: { field: 'contactId', condition: 'is_not_null' },
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editContact',
									conditionalVisibility: { field: 'recordId', condition: 'is_not_null' },
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'clients:contacts:update',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteContact',
									conditionalVisibility: { field: 'recordId', condition: 'is_not_null' },
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'clients:contacts:delete',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-name-column-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Client Contact',
			objectTable: 'client_contact',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'title', fieldLabel: 'Title' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'fax', fieldLabel: 'Fax' },
				{ fieldName: 'extension', fieldLabel: 'Extension' },
				{ fieldName: 'phoneType', fieldLabel: 'Phone Type' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'typeLabel', fieldLabel: 'Type Label' },
				{ fieldName: 'note', fieldLabel: 'Note' },
				{ fieldName: 'isPrimary', fieldLabel: 'Is Primary' },
				{ fieldName: 'mobilePhone', fieldLabel: 'Mobile Phone' },
				{ fieldName: 'roleNames', fieldLabel: 'Roles' },
				{ fieldName: 'recordsLabel', fieldLabel: 'Records' },
				{ fieldName: 'statusLabel', fieldLabel: 'Status' },
				{ fieldName: 'contactId', fieldLabel: 'Contact' },
			],
		},
	],
	tableSorts: [],
};
